import React, { useEffect, useState } from "react";
import { 
  X, 
  Save, 
  Palette, 
  LayoutGrid,
  Info, 
  Settings, 
  CheckCircle2, 
  Loader2,
  Tag,
  Hash,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  createSubject,
  updateSubject,
} from "../../../api/elearning/subjects/subjectInfo";

import {
  showSuccessToast,
  showErrorToast,
  showWarningToast,
} from "../../../components/ConfirmDialog";
import { startSubjectFormTour } from "./subjectFormTour";

// Import các Icon FontAwesome như file gốc của bạn
import {
    faBook, faLaptop, faGraduationCap, faBrain, faShieldAlt,
    faPaintBrush, faUsers, faLightbulb, faCogs, faGlobeAsia,
    faLanguage, faChartLine
} from "@fortawesome/free-solid-svg-icons";

const ICON_MAP = {
    book: faBook, laptop: faLaptop, graduation: faGraduationCap,
    brain: faBrain, shield: faShieldAlt, paint: faPaintBrush,
    users: faUsers, idea: faLightbulb, tech: faCogs,
    global: faGlobeAsia, language: faLanguage, analysis: faChartLine,
};

const ICON_PRESETS = [
    { icon: "book", label: "Sách" }, { icon: "laptop", label: "Máy tính" },
    { icon: "graduation", label: "Giáo dục" }, { icon: "brain", label: "Tư duy" },
    { icon: "shield", label: "An toàn" }, { icon: "paint", label: "Sáng tạo" },  
    { icon: "users", label: "Hợp tác" }, { icon: "idea", label: "Ý tưởng" },
    { icon: "tech", label: "Kỹ thuật" }, { icon: "global", label: "Xã hội" },
    { icon: "language", label: "Ngôn ngữ" }, { icon: "analysis", label: "Phân tích" },
];

const COLOR_PRESETS = [
  "#EF4444", "#F97316", "#FACC15", "#22C55E", "#14B8A6", 
  "#3B82F6", "#6366F1", "#8B5CF6", "#EC4899", "#6B7280"
];

/* ================= COMPONENT PHỤ: SECTION ================= */
const Section = ({ icon: Icon, title, subTitle, children, tourId }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4"
    data-tour={tourId}
  >
    <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
      <div className="p-2 bg-red-50 rounded-lg">
        <Icon className="w-5 h-5 text-red-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">{title}</h3>
        {subTitle && <p className="text-xs text-gray-400">{subTitle}</p>}
      </div>
    </div>
    <div className="pt-2">{children}</div>
  </div>
);

const SubjectFormModal = ({ open, data, onClose }) => {
  const isEdit = !!data?.id;
  const [form, setForm] = useState({
    code: "", name: "", description: "", icon: "book",
    color: "#EF4444", sort_order: 0, status: "active",
  });
  const [loading, setLoading] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);

  useEffect(() => {
    if (data) setForm({ ...form, ...data });
    else setForm({ code: "", name: "", description: "", icon: "book", color: "#EF4444", sort_order: 0, status: "active" });
  }, [data]);

  const handleSubmit = async () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      showWarningToast("Vui lòng điền đầy đủ Mã và Tên môn học");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) await updateSubject(data.id, form);
      else await createSubject(form);
      showSuccessToast(`${isEdit ? "Cập nhật" : "Tạo"} môn học thành công`);
      onClose(true);
    } catch (err) {
      showErrorToast("Không thể lưu môn học");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div 
            className="fixed inset-0 z-[1000] bg-gray-900/60 backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => onClose(false)}
          />

          <motion.div
            className="fixed inset-0 z-[1001] flex items-center justify-center p-4"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
          >
            <div
              className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden border border-white"
              data-tour="subject-form-modal"
            >
              
              {/* HEADER */}
              <div
                className="bg-white px-8 py-5 flex justify-between items-center border-b border-gray-100"
                data-tour="subject-form-header"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
                    <Tag className="text-white w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 leading-none">
                      {isEdit ? "Cập nhật môn học" : "Thêm môn học mới"}
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">Cấu hình thông tin môn học và hệ thống nhận diện</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-tour="subject-form-guide-btn">
                  <button
                    type="button"
                    onClick={() => window.setTimeout(() => startSubjectFormTour(), 80)}
                    title="Hướng dẫn nhanh"
                    aria-label="Hướng dẫn nhanh"
                    className="group relative inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
                  >
                    <Sparkles className="h-4.5 w-4.5" strokeWidth={2.2} />
                    <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
                      Hướng dẫn nhanh
                    </span>
                  </button>
                  <button onClick={() => onClose(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* BODY */}
              <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6 overflow-y-auto max-h-[70vh]">
                
                {/* CỘT TRÁI: THÔNG TIN CHUNG */}
                <div className="space-y-6">
                  <Section
                    icon={Info}
                    title="Thông tin cơ bản"
                    subTitle="Định danh môn học"
                    tourId="subject-form-basic"
                  >
                    <div className="space-y-3">
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Mã môn học *</label>
                        <input
                          value={form.code}
                          disabled={isEdit}
                          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                          placeholder="VD: KNS"
                          className="w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all border-gray-200 disabled:bg-gray-50"
                        />
                      </div>
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Tên môn học *</label>
                        <input
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="VD: Kỹ năng sống"
                          className="w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all border-gray-200 font-semibold"
                        />
                      </div>
                    </div>
                  </Section>

                  <Section
                    icon={Settings}
                    title="Cấu hình"
                    subTitle="Trạng thái & Thứ tự"
                    tourId="subject-form-settings"
                  >
                    <div className="space-y-3">
                      <Select
                        options={[
                          { value: "active", label: "🟢 Đang sử dụng" },
                          { value: "inactive", label: "⚪ Ngưng sử dụng" },
                          { value: "draft", label: "🟠 Sắp triển khai" },
                        ]}
                        value={{ value: form.status, label: form.status === 'active' ? '🟢 Đang sử dụng' : form.status === 'inactive' ? '⚪ Ngưng sử dụng' : '🟠 Sắp triển khai' }}
                        onChange={(opt) => setForm({ ...form, status: opt.value })}
                        styles={customSelectStyles}
                      />
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Thứ tự hiển thị</label>
                        <input
                          type="number"
                          value={form.sort_order}
                          onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                          className="w-full border rounded-xl px-4 py-2.5 border-gray-200 outline-none focus:ring-2 focus:ring-red-100"
                        />
                      </div>
                    </div>
                  </Section>
                </div>

                {/* CỘT PHẢI: NHẬN DIỆN */}
                <div className="space-y-6">
                  <Section
                    icon={Palette}
                    title="Nhận diện"
                    subTitle="Icon và màu sắc đặc trưng"
                    tourId="subject-form-identity"
                  >
                    <div className="flex flex-col items-center p-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                      <div 
                        className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-xl mb-4 transition-all"
                        style={{ backgroundColor: form.color, boxShadow: `0 10px 20px -5px ${form.color}66` }}
                      >
                        <FontAwesomeIcon icon={ICON_MAP[form.icon]} className="text-white text-3xl" />
                      </div>
                      <div className="flex gap-2 w-full">
                        <button 
                          onClick={() => setShowIconPicker(true)}
                          className="flex-1 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-2"
                        >
                          <LayoutGrid size={14} /> Đổi Icon
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-5 gap-2 pt-2">
                      {COLOR_PRESETS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setForm({ ...form, color: c })}
                          className={`h-8 rounded-lg transition-all ${form.color === c ? "ring-2 ring-offset-2 ring-gray-400 scale-110" : "hover:scale-105"}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </Section>

                  <div className="relative" data-tour="subject-form-description">
                    <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Mô tả chi tiết</label>
                    <textarea
                      rows={4}
                      value={form.description || ""}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Nhập ghi chú hoặc mô tả môn học..."
                      className="w-full border rounded-2xl px-4 py-3 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div
                className="bg-white px-8 py-5 flex justify-end gap-4 border-t border-gray-100"
                data-tour="subject-form-actions"
              >
                <button
                  onClick={() => onClose(false)}
                  className="px-8 py-2.5 rounded-xl font-semibold text-gray-500 hover:bg-gray-50 border border-gray-200 transition-all"
                >
                  Huỷ bỏ
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-10 py-2.5 rounded-xl bg-red-600 text-white font-bold flex items-center gap-2 hover:bg-red-700 hover:shadow-lg hover:shadow-red-200 active:scale-95 transition-all disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  {loading ? "Đang lưu..." : "Lưu môn học"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* ICON PICKER MODAL */}
      <AnimatePresence>
        {showIconPicker && (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/20" onClick={() => setShowIconPicker(false)} 
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-lg relative z-10"
            >
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <LayoutGrid className="text-red-600" /> Chọn biểu tượng hiển thị
              </h3>
              <div className="grid grid-cols-4 gap-3">
                {ICON_PRESETS.map((i) => (
                  <button
                    key={i.icon}
                    onClick={() => { setForm({ ...form, icon: i.icon }); setShowIconPicker(false); }}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${form.icon === i.icon ? "border-red-500 bg-red-50 text-red-600" : "border-gray-100 hover:border-gray-200"}`}
                  >
                    <FontAwesomeIcon icon={ICON_MAP[i.icon]} className="text-xl" />
                    <span className="text-[10px] font-bold uppercase">{i.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
};

export default SubjectFormModal;